import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, TextField, MenuItem, Container, Avatar,
  Stack, List, ListItem, ListItemText, ListItemIcon,
  Accordion, AccordionSummary, AccordionDetails, Tooltip
} from '@mui/material';
import {
  ArrowBack, Receipt, Edit, Payment, Cancel,
  Print, Visibility, Person, CalendarToday,
  Badge, LocalHospital, CreditCard,
  Email, Phone, LocationOn,
  ExpandMore, Inventory, AccountBalance,
  LocalOffer, Security, Warning,
  MedicalServices, Science, Description
} from '@mui/icons-material';
import { salesAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';

/**
 * Demo hospital / signature / UPI details
 */
const DEMO_HOSPITAL = {
  name: 'GLOBAL HOSPITAL & RESEARCH CENTER',
  addressLine1: '123 Health Street, Medical City, State - 400001',
  addressLine2: 'Phone: +91 9999 999 999 | Email: info@globalhospital.com',
  gstin: '27AAAAA0000A1Z5',
  logoUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjYwIiB2aWV3Qm94PSIwIDAgMjAwIDYwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iNjAiIGZpbGw9IiMzNjkwZmYiLz48dGV4dCB4PSIxMCIgeT0iMzUiIGZpbGw9IndoaXRlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPkdMT0JBTCAgSE9TUElUQUw8L3RleHQ+PC9zdmc+',
};

const DEMO_SIGNATURES = {
  billingOfficerName: 'Dr. Rajesh Kumar',
  pharmacistName: 'Dr. Priya Sharma',
  billingSignatureUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjYwIiB2aWV3Qm94PSIwIDAgMjAwIDYwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik0xMCAzMEM0MCAxNSw4MCA0NSwxMjAgMzBNMTYwIDE1TDE5MCA0NSIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utd2lkdGg9IjIiLz48L3N2Zz4=',
  pharmacistSignatureUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjYwIiB2aWV3Qm94PSIwIDAgMjAwIDYwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik0xMCA0MEMzMCAyMCw3MCA1MCwxMDAgMzBNMTQwIDIwTDE3MCA1MCIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utd2lkdGg9IjIiLz48L3N2Zz4=',
};

const DEMO_UPI = {
  upiId: 'global.hospital@upi',
  payeeName: 'Global Hospital',
  buildUpiUrl: (amount, invoiceNo, uhid) =>
    `upi://pay?pa=${encodeURIComponent('global.hospital@upi')}&pn=${encodeURIComponent('Global Hospital')}&am=${encodeURIComponent(
      amount?.toFixed?.(2) || ''
    )}&cu=INR&tn=${encodeURIComponent(`INV:${invoiceNo}|UHID:${uhid}`)}`,
};

export const SaleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', sale: null });
  const [paymentData, setPaymentData] = useState({ paidAmount: 0, paymentMethod: 'cash' });
  const [expandedAccordion, setExpandedAccordion] = useState('medicine-details');
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);


  useEffect(() => {
    fetchSale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSale = async () => {
    setLoading(true);
    try {
      const response = await salesAPI.get(id);
      const invoice = response.data.data.salesInvoice;
      // Normalize naming differences between backends
      if (invoice.patient && invoice.patient.address && invoice.patient.address.mobile) {
        invoice.patient.phone = invoice.patient.address.mobile;
      }
      setSale(invoice);
    } catch (error) {
      enqueueSnackbar('Error fetching sale details', { variant: 'error' });
      navigate('/hospital/pharmacy/sales');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Utility helpers ----------
  const currency = (v) => `₹${(Number(v) || 0).toFixed(2)}`;

  // Calculate age from DOB
  const calculateAgeFromDob = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Calculate discount percentage and amount (CORRECTED - based on selling price)
  const calculateDiscountDetails = (item) => {
    const sellingPrice = Number(item.sellingPrice || 0);
    const discountAmount = Number(item.discount || 0);
    const discountPercentage = sellingPrice > 0 ? ((discountAmount / sellingPrice) * 100) : 0;

    return {
      discountAmount,
      discountPercentage: discountPercentage.toFixed(1),
      sellingPrice,
      discountedPrice: sellingPrice - discountAmount
    };
  };

  // Calculate total item discount for the invoice
  const calculateTotalItemDiscount = (invoice) => {
    return invoice.items.reduce((total, item) => {
      return total + (Number(item.discount || 0) * Number(item.quantity || 0));
    }, 0);
  };

  // Calculate total selling price before discounts
  const calculateTotalSellingPrice = (invoice) => {
    return invoice.items.reduce((total, item) => {
      return total + (Number(item.sellingPrice || 0) * Number(item.quantity || 0));
    }, 0);
  };

  // Generate QR code as data URL
  const generateQRCodeDataURL = async (text, size = 120) => {
    try {
      return await QRCode.toDataURL(text, {
        width: size,
        height: size,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('QR Code generation error:', error);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#000000';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('QR Code', size / 2, size / 2);
      return canvas.toDataURL();
    }
  };

  // Load image as data URL
  const loadImageAsDataURL = async (url) => {
    try {
      if (url.startsWith('data:')) {
        return url;
      }
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return url;
    }
  };

  // Build GST breakdown rows
  const buildGstBreakupRows = (invoice) => {
    const map = {};
    invoice.items.forEach((it) => {
      const gst = it.taxRate ?? it.gstRate ?? 0;
      const taxableAmount = Number(it.totalAmount || (it.quantity * (it.sellingPrice - (it.discount || 0))));
      const taxAmt = (taxableAmount * (gst / 100));
      if (!map[gst]) map[gst] = { taxable: 0, tax: 0 };
      map[gst].taxable += taxableAmount;
      map[gst].tax += taxAmt;
    });

    const rows = Object.keys(map).map((gst) => {
      const obj = map[gst];
      return `<tr><td>${gst}%</td><td>₹${obj.taxable.toFixed(2)}</td><td>₹${obj.tax.toFixed(2)}</td></tr>`;
    });

    if (rows.length === 0) {
      return `<tr><td colspan="3">No GST applicable</td></tr>`;
    }
    return rows.join('');
  };

  // Handle accordion change
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // ---------- Enhanced Print Function (A4) ----------
  const handlePrintA4Invoice = async (invoice = sale) => {
    if (!invoice) return;

    try {
      const qrData = `INV:${invoice.invoiceNumber}|UHID:${invoice.uhid || '-'}|TOTAL:${invoice.grandTotal || 0}`;
      const upiUrl = DEMO_UPI.buildUpiUrl(invoice.grandTotal || 0, invoice.invoiceNumber, invoice.uhid);

      const [
        logoDataUrl,
        billingSigDataUrl,
        pharmacistSigDataUrl,
        qrCodeDataUrl,
        upiQrCodeDataUrl
      ] = await Promise.all([
        loadImageAsDataURL(DEMO_HOSPITAL.logoUrl),
        loadImageAsDataURL(DEMO_SIGNATURES.billingSignatureUrl),
        loadImageAsDataURL(DEMO_SIGNATURES.pharmacistSignatureUrl),
        generateQRCodeDataURL(qrData, 80),
        generateQRCodeDataURL(upiUrl, 80)
      ]);

      const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice #${invoice.invoiceNumber}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Inter', sans-serif;
            margin: 10px;
            color: #1a1a1a;
            background: #fff;
            line-height: 1.2;
            font-size: 9px;
          }
          
          .invoice-container {
            max-width: 210mm;
            margin: 0 auto;
            background: white;
            padding: 15px;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #2c5530;
          }
          
          .hospital-info {
            flex: 1;
          }
          
          .hospital-name {
            font-size: 14px;
            font-weight: 700;
            color: #2c5530;
            margin-bottom: 2px;
          }
          
          .hospital-address {
            font-size: 8px;
            color: #666;
            margin-bottom: 1px;
          }
          
          .invoice-meta {
            text-align: right;
            min-width: 120px;
          }
          
          .invoice-number {
            font-size: 11px;
            font-weight: 600;
            color: #2c5530;
            margin-bottom: 2px;
          }
          
          .invoice-date {
            font-size: 8px;
            color: #666;
          }

          .patient-section {
            background: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
            margin-bottom: 12px;
            border-left: 2px solid #2c5530;
            font-size: 8px;
          }

          .patient-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px 12px;
          }

          .patient-field {
            display: flex;
            justify-content: space-between;
            align-items: center;
            min-height: 12px;
          }

          .patient-label {
            font-weight: 600;
            color: #555;
            min-width: 45px;
            text-align: left;
          }

          .patient-value {
            font-weight: 600;
            color: #1a1a1a;
            text-align: right;
            flex: 1;
            margin-left: 8px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .section-title {
            font-size: 10px;
            font-weight: 600;
            color: #2c5530;
            margin-bottom: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #e9ecef;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 8px;
          }
          
          .items-table th {
            background: #2c5530 !important;
            color: white !important;
            padding: 6px 4px;
            text-align: left;
            font-weight: 500;
            font-size: 8px;
          }
          
          .items-table td {
            padding: 4px;
            border-bottom: 0.5px solid #e9ecef;
            vertical-align: top;
          }
          
          .items-table tr:nth-child(even) {
            background: #f8f9fa;
          }
          
          .medicine-name {
            font-weight: 600;
            font-size: 8px;
            line-height: 1.1;
          }
          
          .medicine-details {
            font-size: 7px;
            color: #666;
            line-height: 1;
          }
          
          .batch-info {
            font-size: 7px;
            color: #888;
          }

          .discount-info {
            font-size: 7px;
            color: #d63384;
            font-weight: 600;
          }
          
          .totals-section {
            display: grid;
            grid-template-columns: 1fr 200px;
            gap: 15px;
            margin: 15px 0;
          }
          
          .gst-summary {
            background: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
          }
          
          .gst-summary table {
            width: 100%;
            font-size: 8px;
            border-collapse: collapse;
          }
          
          .gst-summary th {
            background: #e9ecef !important;
            color: #000 !important;
            padding: 4px;
            text-align: left;
            font-size: 8px;
          }
          
          .gst-summary td {
            padding: 4px;
            border-bottom: 0.5px solid #ddd;
            font-size: 8px;
          }
          
          .amount-summary {
            background: #2c5530 !important;
            color: white !important;
            padding: 10px;
            border-radius: 4px;
            font-size: 8px;
          }
          
          .amount-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          
          .grand-total {
            font-size: 10px;
            font-weight: 700;
            border-top: 0.5px solid rgba(255,255,255,0.3);
            padding-top: 4px;
            margin-top: 4px;
          }
          
          .signatures {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin: 20px 0 10px 0;
            font-size: 8px;
          }
          
          .signature-box {
            text-align: center;
          }
          
          .signature-line {
            border-top: 0.5px solid #1a1a1a;
            margin: 20px 0 5px 0;
          }
          
          .footer {
            text-align: center;
            font-size: 7px;
            color: #666;
            margin-top: 15px;
            padding-top: 8px;
            border-top: 0.5px solid #e9ecef;
          }
          
          .qr-section {
            display: flex;
            justify-content: space-around;
            align-items: center;
            margin: 15px 0;
            padding: 10px;
            background: #f8f9fa;
            border-radius: 4px;
          }
          
          .qr-box {
            text-align: center;
          }
          
          .qr-title {
            font-size: 8px;
            font-weight: 600;
            margin-bottom: 4px;
            color: #2c5530;
          }
          
          .barcode-container {
            margin-top: 5px;
            text-align: center;
          }
          
          .compact-spacing {
            margin: 0;
            padding: 0;
          }
          
          .tiny-text {
            font-size: 7px;
          }

          .profit-info {
            font-size: 7px;
            color: #198754;
            font-weight: 600;
          }
          
          @media print {
            body { 
              margin: 5px; 
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-size: 9px !important;
            }
            .invoice-container { 
              padding: 10px;
            }
            .items-table th {
              background: #2c5530 !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
            }
            .amount-summary {
              background: #2c5530 !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
            }
            .gst-summary th {
              background: #e9ecef !important;
              color: #000 !important;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <!-- Header Section - Compact -->
          <div class="header">
            <div class="hospital-info">
              <div class="hospital-name">${DEMO_HOSPITAL.name}</div>
              <div class="hospital-address">${DEMO_HOSPITAL.addressLine1}</div>
              <div class="hospital-address">${DEMO_HOSPITAL.addressLine2}</div>
              <div class="hospital-address">GSTIN: ${DEMO_HOSPITAL.gstin}</div>
            </div>
            
            <div class="invoice-meta">
              <div class="invoice-number">INV #${invoice.invoiceNumber}</div>
              <div class="invoice-date">${new Date(invoice.saleDate).toLocaleDateString('en-IN')}</div>
              <div class="invoice-date">${new Date(invoice.saleDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
              <div class="barcode-container">
                <svg id="barcodeA4"></svg>
              </div>
            </div>
          </div>
          
          <!-- Patient Details - Compact -->
          <div class="patient-section">
            <div class="patient-grid">
              <div class="patient-field">
                <span class="patient-label">Name:</span>
                <span class="patient-value">${(invoice.patient?.first_name || invoice.patient?.firstName || '-').substring(0, 15)} ${(invoice.patient?.last_name || invoice.patient?.lastName || '').substring(0, 10)}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">UHID:</span>
                <span class="patient-value">${invoice.uhid || '-'}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">OP No:</span>
                <span class="patient-value">${invoice.op_no || invoice.opNo || '-'}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">IPD No:</span>
                <span class="patient-value">${invoice.ipd_no || invoice.ipdNo || '-'}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">Type:</span>
                <span class="patient-value">${invoice.patientType || 'OPD'}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">Gender/Age:</span>
                <span class="patient-value">${invoice.patient?.gender || '-'}/${invoice.patient?.age || (invoice.patient?.dob ? calculateAgeFromDob(invoice.patient.dob) : '-')}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">Mobile:</span>
                <span class="patient-value">${invoice.patient?.phone || invoice.patient?.address?.mobile || '-'}</span>
              </div>
              <div class="patient-field">
                <span class="patient-label">Status:</span>
                <span class="patient-value">${invoice.paymentStatus || 'pending'}</span>
              </div>
            </div>
          </div>
          
          <!-- Items Table - Enhanced with Discount and HSN -->
          <div class="section-title">MEDICINES (${invoice.items.length} items)</div>
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 5%">#</th>
                <th style="width: 30%">Medicine Details</th>
                <th style="width: 10%">Batch</th>
                <th style="width: 8%">Expiry</th>
                <th style="width: 6%">Qty</th>
                <th style="width: 8%">MRP</th>
                <th style="width: 8%">Price</th>
                <th style="width: 8%">Disc.</th>
                <th style="width: 6%">GST%</th>
                <th style="width: 8%">HSN</th>
                <th style="width: 9%">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items.map((it, idx) => {
        const discountDetails = calculateDiscountDetails(it);
        return `
                <tr>
                  <td>${idx + 1}</td>
                  <td>
                    <div class="medicine-name">${(it.medicine?.name || '—').substring(0, 30)}</div>
                    <div class="medicine-details">${it.medicine?.genericName ? it.medicine.genericName.substring(0, 25) : ''}</div>
                    <div class="medicine-details">${it.medicine?.manufacturer ? it.medicine.manufacturer.substring(0, 25) : ''}</div>
                  </td>
                  <td class="batch-info">${it.batchNumber || '-'}</td>
                  <td class="batch-info">${it.expiryDate ? new Date(it.expiryDate).toLocaleDateString('en-IN').replace(/\//g, '-') : '-'}</td>
                  <td style="text-align: center;">${it.quantity}</td>
                  <td style="text-align: right;">${it.mrp ? Number(it.mrp).toFixed(0) : '-'}</td>
                  <td style="text-align: right;">${it.sellingPrice ? Number(it.sellingPrice).toFixed(0) : '-'}</td>
                  <td class="discount-info" style="text-align: center;">
                    ${discountDetails.discountAmount > 0 ? `${discountDetails.discountPercentage}%` : '-'}
                  </td>
                  <td style="text-align: center;">${typeof it.taxRate !== 'undefined' ? it.taxRate : (it.gstRate ? it.gstRate : '—')}</td>
                  <td style="text-align: center; font-size: 7px;">${it.medicine?.hsnCode || it.hsn || '—'}</td>
                  <td style="text-align: right; font-weight: 600;">${(Number(it.totalAmount) || (Number(it.quantity || 0) * Number(it.sellingPrice || 0))).toFixed(0)}</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>
          
          <!-- Enhanced Totals and GST Section -->
          <div class="totals-section">
            <div class="gst-summary">
              <div class="section-title">DETAILED BREAKUP</div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style="text-align: right;">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total Selling Price</td>
                    <td style="text-align: right;">${calculateTotalSellingPrice(invoice).toFixed(0)}</td>
                  </tr>
                  <tr>
                    <td>Total Item Discount</td>
                    <td style="text-align: right; color: #d63384;">-${calculateTotalItemDiscount(invoice).toFixed(0)}</td>
                  </tr>
                  <tr>
                    <td>Taxable Amount</td>
                    <td style="text-align: right;">${Number(invoice.subtotal || 0).toFixed(0)}</td>
                  </tr>
                  ${buildGstBreakupRows(invoice).split('</tr>').map(row => `
                    ${row}</tr>
                  `).join('')}
                  <tr style="border-top: 1px solid #ddd;">
                    <td><strong>Total Tax</strong></td>
                    <td style="text-align: right;"><strong>${Number(invoice.taxAmount || 0).toFixed(0)}</strong></td>
                  </tr>
                  <tr class="profit-info">
                    <td>Total Profit</td>
                    <td style="text-align: right;">+${Number(invoice.totalProfit || 0).toFixed(0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="amount-summary">
              <div class="section-title" style="color: white; border-bottom: 0.5px solid rgba(255,255,255,0.3);">PAYMENT SUMMARY</div>
              <div class="amount-row">
                <span>Subtotal:</span>
                <span>${Number(invoice.subtotal || 0).toFixed(0)}</span>
              </div>
              <div class="amount-row">
                <span>Tax:</span>
                <span>${Number(invoice.taxAmount || 0).toFixed(0)}</span>
              </div>
              <div class="amount-row">
                <span>Invoice Discount:</span>
                <span>-${Number(invoice.discount || 0).toFixed(0)}</span>
              </div>
              <div class="amount-row grand-total">
                <span>TOTAL:</span>
                <span>${Number(invoice.grandTotal || 0).toFixed(0)}</span>
              </div>
              <div class="amount-row">
                <span>Paid:</span>
                <span>${Number(invoice.paidAmount || 0).toFixed(0)}</span>
              </div>
              <div class="amount-row" style="color: ${invoice.dueAmount > 0 ? '#ff6b6b' : '#51cf66'}">
                <span>Due:</span>
                <span>${Number(invoice.dueAmount || Math.max(0, (invoice.grandTotal || 0) - (invoice.paidAmount || 0))).toFixed(0)}</span>
              </div>
              <div class="amount-row tiny-text">
                <span>${(invoice.paymentStatus || 'pending').toUpperCase()}</span>
                <span>|</span>
                <span>${(invoice.paymentMethod || 'cash').toUpperCase()}</span>
              </div>
            </div>
          </div>
          
          <!-- QR Codes - Compact -->
          <div class="qr-section">
            <div class="qr-box">
              <div class="qr-title">INVOICE QR</div>
              <img src="${qrCodeDataUrl}" style="width: 60px; height: 60px;" alt="Invoice QR">
            </div>
            
            <div class="qr-box">
              <div class="qr-title">UPI PAYMENT</div>
              <img src="${upiQrCodeDataUrl}" style="width: 60px; height: 60px;" alt="UPI QR">
              <div class="tiny-text">${DEMO_UPI.upiId}</div>
            </div>
          </div>
          
          <!-- Signatures - Compact -->
          <div class="signatures">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div style="margin-top: 3px; font-weight: 600; font-size: 8px;">${DEMO_SIGNATURES.billingOfficerName}</div>
              <div class="tiny-text">Billing Officer</div>
            </div>
            
            <div class="signature-box">
              <div class="signature-line"></div>
              <div style="margin-top: 3px; font-weight: 600; font-size: 8px;">${DEMO_SIGNATURES.pharmacistName}</div>
              <div class="tiny-text">Pharmacist</div>
            </div>
          </div>
          
          <!-- Footer - Compact -->
          <div class="footer">
            <div>Computer generated invoice • No signature required</div>
            <div class="tiny-text">Generated: ${new Date().toLocaleString('en-IN')}</div>
          </div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcodeA4", "${invoice.invoiceNumber}", {
                format: "CODE128",
                width: 1.2,
                height: 30,
                displayValue: false,
                margin: 0,
                background: "#ffffff",
                lineColor: "#000000"
              });
              
              // Auto print immediately
              setTimeout(() => {
                window.print();
                // Close iframe after print
                setTimeout(() => {
                  window.frameElement?.remove();
                }, 100);
              }, 100);
            } catch(e) {
              console.error('Barcode error:', e);
              // Print even if barcode fails
              setTimeout(() => {
                window.print();
                setTimeout(() => {
                  window.frameElement?.remove();
                }, 100);
              }, 100);
            }
          };
        </script>
      </body>
      </html>
    `;
      if (isMobile) {
        const win = window.open("", "_blank");
        win.document.write(html);
        win.document.close();
        win.onload = () => setTimeout(() => win.print(), 500);
        return;
      } else {
        // Create hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.write(html);
        iframeDoc.close();
      }


    } catch (error) {
      console.error('A4 Print error:', error);
      enqueueSnackbar('Error generating A4 print document', { variant: 'error' });
    }
  };

  // ---------- Printing: Thermal Format (58mm) ----------
  const handlePrintThermal = async (invoice = sale) => {
    if (!invoice) return;

    try {
      const qrData = `INV:${invoice.invoiceNumber}|UHID:${invoice.uhid || '-'}|TOTAL:${invoice.grandTotal || 0}`;
      const upiUrl = DEMO_UPI.buildUpiUrl(
        invoice.grandTotal || 0,
        invoice.invoiceNumber,
        invoice.uhid
      );

      const [
        qrCodeDataUrl,
        upiQrCodeDataUrl
      ] = await Promise.all([
        generateQRCodeDataURL(qrData, 80),
        generateQRCodeDataURL(upiUrl, 80)
      ]);

      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
<title>Receipt #${invoice.invoiceNumber}</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;600&display=swap');
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Roboto Mono',monospace;width:58mm;padding:5px;background:white;color:#000;font-size:9px;line-height:1.2;}
.thermal-container{width:100%;max-width:58mm;}
.header{text-align:center;margin-bottom:5px;padding-bottom:5px;border-bottom:1px dashed #000;}
.hospital-name{font-size:10px;font-weight:700;margin-bottom:2px;text-transform:uppercase;}
.hospital-address{font-size:7px;margin-bottom:1px;}
.invoice-meta{text-align:center;margin:5px 0;}
.invoice-number{font-size:9px;font-weight:700;margin-bottom:2px;}
.invoice-date{font-size:8px;}
.patient-section{margin:5px 0;padding:3px;border:1px dashed #666;}
.patient-title{font-weight:700;text-align:center;margin-bottom:3px;font-size:8px;}
.patient-details{font-size:7px;}
.patient-row{display:flex;justify-content:space-between;margin-bottom:1px;}
.items-table{width:100%;border-collapse:collapse;margin:5px 0;font-size:7px;}
.items-table th{text-align:left;padding:2px 1px;border-bottom:1px dashed #000;font-weight:700;}
.items-table td{padding:2px 1px;border-bottom:1px dotted #ccc;}
.items-table .name{max-width:25mm;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.totals-section{margin:5px 0;border-top:1px dashed #000;padding-top:3px;}
.total-row{display:flex;justify-content:space-between;margin-bottom:1px;font-size:8px;}
.grand-total{font-weight:700;border-top:1px solid #000;padding-top:2px;margin-top:2px;}
.due-amount{color:#ff0000;font-weight:700;}
.qr-section{text-align:center;margin:5px 0;padding:3px;border:1px dashed #000;}
.qr-title{font-weight:700;font-size:7px;margin-bottom:2px;}
.qr-container{display:flex;justify-content:space-around;margin:3px 0;}
.qr-box{text-align:center;}
.footer{text-align:center;font-size:6px;margin-top:5px;padding-top:3px;border-top:1px dashed #000;}
.barcode{text-align:center;margin:3px 0;}
.divider{border-top:1px dashed #000;margin:3px 0;}
.double-divider{border-top:2px solid #000;margin:4px 0;}
.center{text-align:center;}
.bold{font-weight:700;}
.uppercase{text-transform:uppercase;}
@media print{
  body{width:58mm!important;margin:0!important;padding:3px!important;}
  .thermal-container{width:58mm!important;}
}
</style>
</head>
<body>
  <div class="thermal-container">
    <div class="header">
      <div class="hospital-name">${DEMO_HOSPITAL.name.split('&')[0]}</div>
      <div class="hospital-address">${DEMO_HOSPITAL.addressLine1.split(',')[0]}</div>
      <div class="hospital-address">GST: ${DEMO_HOSPITAL.gstin}</div>
    </div>
    <div class="double-divider"></div>
    <div class="invoice-meta">
      <div class="invoice-number">INVOICE #${invoice.invoiceNumber}</div>
      <div class="invoice-date">
        ${new Date(invoice.saleDate).toLocaleDateString('en-IN')}
        ${new Date(invoice.saleDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
    <div class="divider"></div>
    <div class="barcode"><svg id="barcodeThermal"></svg></div>
    <div class="divider"></div>
    <div class="patient-section">
      <div class="patient-title">PATIENT DETAILS</div>
      <div class="patient-details">
        <div class="patient-row"><span>Name:</span><span class="bold">${(invoice.patient?.first_name || 'Walk-in').substring(0, 12)}</span></div>
        <div class="patient-row"><span>UHID:</span><span>${invoice.uhid || '-'}</span></div>
        <div class="patient-row"><span>OP No:</span><span>${invoice.op_no || '-'}</span></div>
        <div class="patient-row"><span>Type:</span><span class="bold uppercase">${invoice.patientType || 'OPD'}</span></div>
        <div class="patient-row"><span>Gender/Age:</span>
          <span>${invoice.patient?.gender?.substring(0, 1) || '-'} / ${invoice.patient?.age || '-'}</span>
        </div>
      </div>
    </div>
    <div class="divider"></div>
    <table class="items-table">
      <thead>
        <tr>
          <th>Item</th><th>Qty</th><th>Price</th><th>Disc</th><th>Amt</th>
        </tr>
      </thead>
      <tbody>
        ${invoice.items.map((it) => {
        const discountDetails = calculateDiscountDetails(it);
        return `
          <tr>
            <td class="name">${(it.medicine?.name || 'Item').substring(0, 18)}</td>
            <td>${it.quantity}</td>
            <td>${Number(it.sellingPrice || 0).toFixed(0)}</td>
            <td>${discountDetails.discountAmount > 0 ? `${discountDetails.discountPercentage}%` : '-'}</td>
            <td class="bold">${Number(it.totalAmount || it.quantity * it.sellingPrice).toFixed(0)}</td>
          </tr>
        `}).join('')}
      </tbody>
    </table>
    <div class="divider"></div>
    <div class="totals-section">
      <div class="total-row"><span>Subtotal:</span><span>${currency(invoice.subtotal)}</span></div>
      <div class="total-row"><span>Item Disc:</span><span>-${currency(calculateTotalItemDiscount(invoice))}</span></div>
      <div class="total-row"><span>Tax:</span><span>${currency(invoice.taxAmount)}</span></div>
      <div class="total-row"><span>Inv Discount:</span><span>-${currency(invoice.discount)}</span></div>
      <div class="total-row grand-total"><span>TOTAL:</span><span>${currency(invoice.grandTotal)}</span></div>
      <div class="total-row"><span>Paid:</span><span>${currency(invoice.paidAmount)}</span></div>
      <div class="total-row due-amount"><span>DUE:</span>
        <span>${currency(invoice.dueAmount || invoice.grandTotal - invoice.paidAmount)}</span>
      </div>
      <div class="total-row"><span>Status:</span><span class="uppercase">${invoice.paymentStatus || 'pending'}</span></div>
      <div class="total-row"><span>Method:</span><span>${invoice.paymentMethod || 'cash'}</span></div>
    </div>
    <div class="divider"></div>
    <div class="qr-section">
      <div class="qr-title">QUICK PAYMENTS</div>
      <div class="qr-container">
        <div class="qr-box">
          <img src="${qrCodeDataUrl}" width="35" height="35" />
          <div style="font-size:5px;">INVOICE QR</div>
        </div>
        <div class="qr-box">
          <img src="${upiQrCodeDataUrl}" width="35" height="35" />
          <div style="font-size:5px;">PAY UPI</div>
        </div>
      </div>
      <div style="font-size:6px;">UPI: ${DEMO_UPI.upiId}</div>
    </div>
    <div class="divider"></div>
    <div class="footer">
      <div>** COMPUTER GENERATED RECEIPT **</div>
      <div>No signature required</div>
      <div>Thank you for your visit!</div>
    </div>
  </div>
  <script>
    window.onload = function() {
      JsBarcode("#barcodeThermal", "${invoice.invoiceNumber}", {
        format:"CODE128",
        width:0.8,
        height:25,
        displayValue:true,
        font:"Roboto Mono",
        fontSize:8,
        margin:0
      });
      setTimeout(() => { window.print(); }, 300);
      setTimeout(() => { window.close(); }, 800);
    };
  </script>
</body>
</html>
`;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();

      iframe.onload = () => {
        setTimeout(() => document.body.removeChild(iframe), 2000);
      };

    } catch (error) {
      console.error("Thermal print error:", error);
      enqueueSnackbar("Error generating thermal receipt", { variant: "error" });
    }
  };

  // ---------- PDF Download ----------
  // ---------- PDF Download ----------
  const handleDownloadPdf = async (invoice = sale) => {
    if (!invoice) return;

    try {
      // Import jsPDF and autoTable dynamically to ensure proper loading
      const { default: JsPDF } = await import('jspdf');
      const autoTable = await import('jspdf-autotable');

      // Initialize PDF
      const doc = new JsPDF('p', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;
      let yPosition = margin;

      // Add hospital header
      doc.setFontSize(20);
      doc.setTextColor(44, 85, 48);
      doc.text(DEMO_HOSPITAL.name, margin, yPosition);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      yPosition += 20;
      doc.text(DEMO_HOSPITAL.addressLine1, margin, yPosition);
      yPosition += 12;
      doc.text(DEMO_HOSPITAL.addressLine2, margin, yPosition);
      yPosition += 12;
      doc.text(`GSTIN: ${DEMO_HOSPITAL.gstin}`, margin, yPosition);

      // Invoice info on right
      doc.setFontSize(16);
      doc.setTextColor(44, 85, 48);
      doc.text(`INVOICE #${invoice.invoiceNumber}`, pageWidth - margin - 150, margin);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${new Date(invoice.saleDate).toLocaleDateString()}`, pageWidth - margin - 150, margin + 16);

      yPosition += 30;

      // Patient details
      doc.setFontSize(14);
      doc.setTextColor(44, 85, 48);
      doc.text('PATIENT DETAILS', margin, yPosition);
      yPosition += 20;

      const patientDetails = [
        [`Name: ${invoice.patient?.first_name || invoice.patient?.firstName || '-'} ${invoice.patient?.last_name || invoice.patient?.lastName || ''}`,
        `UHID: ${invoice.uhid || '-'}`],
        [`OP No: ${invoice.op_no || invoice.opNo || '-'}`,
        `IPD No: ${invoice.ipd_no || invoice.ipdNo || '-'}`],
        [`Patient Type: ${invoice.patientType || 'OPD'}`,
        `Gender/Age: ${invoice.patient?.gender || '-'} / ${invoice.patient?.age || (invoice.patient?.dob ? calculateAgeFromDob(invoice.patient.dob) : '-')}`],
        [`Mobile: ${invoice.patient?.phone || invoice.patient?.address?.mobile || '-'}`, '']
      ];

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);

      patientDetails.forEach(([left, right]) => {
        doc.text(left, margin, yPosition);
        if (right) {
          doc.text(right, pageWidth / 2, yPosition);
        }
        yPosition += 15;
      });

      yPosition += 10;

      // Enhanced Items table with more details
      const columns = [
        { header: '#', dataKey: 'sr' },
        { header: 'Medicine', dataKey: 'medicine' },
        { header: 'Batch', dataKey: 'batch' },
        { header: 'Expiry', dataKey: 'expiry' },
        { header: 'Qty', dataKey: 'qty' },
        { header: 'MRP', dataKey: 'mrp' },
        { header: 'Price', dataKey: 'price' },
        { header: 'Disc%', dataKey: 'discount' },
        { header: 'GST%', dataKey: 'gst' },
        { header: 'HSN', dataKey: 'hsn' },
        { header: 'Amount', dataKey: 'amount' },
      ];

      const rows = invoice.items.map((it, idx) => {
        const discountDetails = calculateDiscountDetails(it);
        return {
          sr: idx + 1,
          medicine: it.medicine?.name || '-',
          batch: it.batchNumber || '-',
          expiry: it.expiryDate ? new Date(it.expiryDate).toLocaleDateString() : '-',
          qty: it.quantity,
          mrp: it.mrp ? Number(it.mrp).toFixed(0) : '-',
          price: it.sellingPrice ? Number(it.sellingPrice).toFixed(0) : '-',
          discount: discountDetails.discountAmount > 0 ? `${discountDetails.discountPercentage}%` : '-',
          gst: typeof it.taxRate !== 'undefined' ? `${it.taxRate}%` : (it.gstRate ? `${it.gstRate}%` : '-'),
          hsn: it.medicine?.hsnCode || '-',
          amount: Number(it.totalAmount || (it.quantity * (it.sellingPrice || 0))).toFixed(0),
        };
      });

      // Use autoTable with proper method
      autoTable(doc, {
        startY: yPosition,
        head: [columns.map(c => c.header)],
        body: rows.map(r => columns.map(c => r[c.dataKey])),
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [44, 85, 48],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        }
      });

      const finalY = doc.lastAutoTable?.finalY || yPosition + 300;
      const adjustedFinalY = finalY + 20;

      // Enhanced Totals with more details
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      // Left side - Detailed breakdown
      doc.text(`Total Selling Price: ${currency(calculateTotalSellingPrice(invoice))}`, margin, adjustedFinalY);
      doc.text(`Total Item Discount: -${currency(calculateTotalItemDiscount(invoice))}`, margin, adjustedFinalY + 15);
      doc.text(`Taxable Amount: ${currency(invoice.subtotal)}`, margin, adjustedFinalY + 30);
      doc.text(`Total Tax: ${currency(invoice.taxAmount || 0)}`, margin, adjustedFinalY + 45);
      doc.text(`Cost Price: ${currency(invoice.totalCost || 0)}`, margin, adjustedFinalY + 60);
      doc.setTextColor(0, 150, 0);
      doc.text(`Total Profit: +${currency(invoice.totalProfit || 0)}`, margin, adjustedFinalY + 75);

      // Right side - Payment summary
      doc.setTextColor(0, 0, 0);
      doc.text(`Subtotal: ${currency(invoice.subtotal)}`, pageWidth - margin - 150, adjustedFinalY);
      doc.text(`Tax: ${currency(invoice.taxAmount || 0)}`, pageWidth - margin - 150, adjustedFinalY + 15);
      doc.text(`Invoice Discount: -${currency(invoice.discount || 0)}`, pageWidth - margin - 150, adjustedFinalY + 30);
      doc.setFontSize(13);
      doc.setTextColor(44, 85, 48);
      doc.text(`Grand Total: ${currency(invoice.grandTotal)}`, pageWidth - margin - 150, adjustedFinalY + 50);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Paid: ${currency(invoice.paidAmount || 0)}`, pageWidth - margin - 150, adjustedFinalY + 70);
      const dueColor = invoice.dueAmount > 0 ? [200, 0, 0] : [0, 150, 0];
      doc.setTextColor(...dueColor);
      doc.text(`Due: ${currency(invoice.dueAmount || Math.max(0, (invoice.grandTotal || 0) - (invoice.paidAmount || 0)))}`, pageWidth - margin - 150, adjustedFinalY + 85);

      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 40;
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text('This is a computer generated invoice. No signature is required.', margin, footerY);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, footerY + 12);

      doc.save(`Invoice-${invoice.invoiceNumber}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      enqueueSnackbar('Error generating PDF', { variant: 'error' });
    }
  };

  // ---------- Update payment handlers ----------
  const handleUpdatePayment = () => {
    setPaymentData({
      paidAmount: sale.paidAmount,
      paymentMethod: sale.paymentMethod,
    });
    setActionDialog({
      open: true,
      type: 'payment',
      sale: sale,
    });
  };

  const handleConfirmPaymentUpdate = async () => {
    try {
      const { grandTotal } = sale;
      const paidAmount = paymentData.paidAmount;
      const dueAmount = Math.max(0, grandTotal - paidAmount);

      let paymentStatus = 'pending';
      if (dueAmount === 0 && paidAmount > 0) {
        paymentStatus = 'paid';
      } else if (dueAmount > 0 && dueAmount < grandTotal) {
        paymentStatus = 'partially_paid';
      }

      await salesAPI.updatePayment(sale._id, {
        paidAmount,
        paymentMethod: paymentData.paymentMethod,
        paymentStatus,
      });

      enqueueSnackbar('Payment updated successfully', { variant: 'success' });
      fetchSale();
      setActionDialog({ open: false, type: '', sale: null });
    } catch (error) {
      enqueueSnackbar('Error updating payment', { variant: 'error' });
    }
  };

  const handleCancelSale = () => {
    setActionDialog({
      open: true,
      type: 'cancel',
      sale: sale,
    });
  };

  const handleConfirmCancel = async () => {
    try {
      await salesAPI.cancel(sale._id);
      enqueueSnackbar('Sale cancelled successfully', { variant: 'success' });
      navigate('/hospital/pharmacy/sales');
    } catch (error) {
      enqueueSnackbar('Error cancelling sale', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h4" sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  if (!sale) {
    return (
      <Alert severity="error" sx={{ m: 2, fontSize: '1.1rem' }}>
        Sale not found
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)' }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/hospital/pharmacy/sales')} sx={{ fontSize: '1rem' }}>
            Back to Sales
          </Button>

          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>
            Invoice #{sale.invoiceNumber}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate(`/hospital/pharmacy/sales/${sale._id}/edit`)} disabled={sale.status !== 'confirmed'}>
              Edit
            </Button>
            <Button variant="outlined" startIcon={<Payment />} onClick={handleUpdatePayment} disabled={sale.status !== 'confirmed'}>
              Update Payment
            </Button>
            <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={handleCancelSale} disabled={sale.status !== 'confirmed'}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<Print />} onClick={() => handlePrintA4Invoice(sale)}>
              Print A4
            </Button>
            <Button variant="contained" color="primary" onClick={() => handlePrintThermal(sale)}>
              Print Thermal
            </Button>
            <Button variant="contained" color="secondary" onClick={() => handleDownloadPdf(sale)}>
              Download PDF
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Patient Details Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  Patient Details
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Badge color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Patient Name"
                      secondary={`${sale.patient?.first_name || sale.patient?.firstName || '-'} ${sale.patient?.last_name || sale.patient?.lastName || ''}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Badge color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="UHID" secondary={sale.uhid || '-'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocalHospital color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="OP Number" secondary={sale.op_no || sale.opNo || '-'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocalHospital color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="IPD Number" secondary={sale.ipd_no || sale.ipdNo || '-'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarToday color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Gender / Age"
                      secondary={`${sale.patient?.gender || '-'} / ${sale.patient?.age || (sale.patient?.dob ? calculateAgeFromDob(sale.patient.dob) : '-')}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Phone color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Mobile" secondary={sale.patient?.phone || sale.patient?.address?.mobile || '-'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CreditCard color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Patient Type"
                      secondary={
                        <Chip
                          label={sale.patientType || 'OPD'}
                          color={sale.patientType === 'IPD' ? 'secondary' : 'primary'}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Enhanced Summary Card */}
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>Financial Summary</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Selling Price:</Typography>
                    <Typography fontWeight="600">
                      {currency(calculateTotalSellingPrice(sale))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Item Discount:</Typography>
                    <Typography fontWeight="600" color="green">
                      -{currency(calculateTotalItemDiscount(sale))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Subtotal:</Typography>
                    <Typography fontWeight="600">{currency(sale.subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Tax:</Typography>
                    <Typography fontWeight="600">{currency(sale.taxAmount || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Invoice Discount:</Typography>
                    <Typography fontWeight="600" color="green">{currency(sale.discount || 0)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Grand Total:</Typography>
                    <Typography variant="h6" color="primary">{currency(sale.grandTotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Cost Price:</Typography>
                    <Typography fontWeight="600">{currency(sale.totalCost || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Profit:</Typography>
                    <Typography fontWeight="600" color="success.main">
                      +{currency(sale.totalProfit || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Paid:</Typography>
                    <Typography fontWeight="600" color="green">{currency(sale.paidAmount || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Due:</Typography>
                    <Typography fontWeight="600" color="error">
                      {currency(sale.dueAmount || Math.max(0, (sale.grandTotal || 0) - (sale.paidAmount || 0)))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2">Status:</Typography>
                    <Chip
                      label={sale.paymentStatus?.toUpperCase() || 'PENDING'}
                      color={
                        sale.paymentStatus === 'paid' ? 'success' :
                          sale.paymentStatus === 'partially_paid' ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Payment Method:</Typography>
                    <Chip
                      label={(sale.paymentMethod || 'cash').toUpperCase()}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  <Button variant="contained" size="small" onClick={() => handlePrintA4Invoice(sale)} fullWidth>
                    Print A4
                  </Button>
                  <Button variant="contained" size="small" onClick={() => handlePrintThermal(sale)} fullWidth>
                    Print Thermal
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => handleDownloadPdf(sale)} fullWidth>
                    Download PDF
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Enhanced Items Section with Accordions */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalServices color="primary" />
                  Medicine Items ({sale.items.length})
                </Typography>

                {sale.items.map((item, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedAccordion === `medicine-${index}`}
                    onChange={handleAccordionChange(`medicine-${index}`)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                            {item.medicine?.name || 'Unknown Medicine'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.medicine?.genericName} • Batch: {item.batchNumber} • Qty: {item.quantity}
                          </Typography>
                        </Box>
                        <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem' }}>
                          {currency(item.totalAmount || (item.quantity * item.sellingPrice))}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description color="primary" />
                            Basic Information
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Brand:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.brand || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.manufacturer || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Form:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.form || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Strength:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.strength || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Category:</Typography>
                              <Chip
                                label={item.medicine?.category || '-'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Pricing & Batch Details */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountBalance color="primary" />
                            Pricing & Batch
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">MRP:</Typography>
                              <Typography variant="body2" fontWeight="600">{currency(item.mrp)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Selling Price:</Typography>
                              <Typography variant="body2" fontWeight="600">{currency(item.sellingPrice)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Cost Price:</Typography>
                              <Typography variant="body2" fontWeight="600">{currency(item.costPrice)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Discount:</Typography>
                              <Tooltip title={`₹${calculateDiscountDetails(item).discountAmount.toFixed(2)} per unit`}>
                                <Chip
                                  label={`${calculateDiscountDetails(item).discountPercentage}%`}
                                  size="small"
                                  color="success"
                                />
                              </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Profit:</Typography>
                              <Typography variant="body2" fontWeight="600" color="success.main">
                                +{currency(item.profit || (item.sellingPrice - item.costPrice))}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Expiry:</Typography>
                              <Chip
                                label={item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                                size="small"
                                color={item.expiryDate && new Date(item.expiryDate) < new Date() ? 'error' : 'default'}
                                variant="outlined"
                              />
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Tax & Regulatory Information */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalOffer color="primary" />
                            Tax & Regulatory
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">GST Rate:</Typography>
                              <Chip
                                label={`${item.taxRate || item.gstRate || 0}%`}
                                size="small"
                                color="secondary"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">HSN Code:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.hsnCode || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Schedule:</Typography>
                              <Chip
                                label={item.medicine?.schedule ? item.medicine.schedule.replace('_', ' ').toUpperCase() : 'NON-SCHEDULED'}
                                size="small"
                                color={item.medicine?.schedule === 'non_scheduled' ? 'default' : 'warning'}
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">ATC Code:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.atcCode || '-'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Medical Information */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Science color="primary" />
                            Medical Information
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Therapeutic Class:</Typography>
                              <Typography variant="body2" fontWeight="600">{item.medicine?.therapeuticClass || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Description:</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {item.medicine?.description || 'No description available'}
                              </Typography>
                            </Box>
                            {item.medicine?.sideEffects && item.medicine.sideEffects.length > 0 && (
                              <Box>
                                <Typography variant="body2" color="text.secondary">Side Effects:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {item.medicine.sideEffects.map((effect, idx) => (
                                    <Chip key={idx} label={effect} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Grid>

                        {/* Storage Information */}
                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Inventory color="primary" />
                            Storage Conditions
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<Warning />}
                              label={`Temperature: ${item.medicine?.storageConditions?.temperature || 'Room Temperature'}`}
                              variant="outlined"
                            />
                            <Chip
                              label={`Humidity: ${item.medicine?.storageConditions?.humidity || 'Normal'}`}
                              variant="outlined"
                            />
                            {item.medicine?.storageConditions?.lightSensitive && (
                              <Chip label="Light Sensitive" color="warning" size="small" />
                            )}
                            {item.medicine?.storageConditions?.refrigerated && (
                              <Chip label="Refrigerated" color="info" size="small" />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>

            {/* Additional Information Card */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security color="primary" />
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Invoice Metadata</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                        <Typography variant="body2">
                          {new Date(sale.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Sale Date:</Typography>
                        <Typography variant="body2">
                          {new Date(sale.saleDate).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip
                          label={sale.status?.toUpperCase() || 'CONFIRMED'}
                          color={sale.status === 'confirmed' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Version:</Typography>
                        <Typography variant="body2">v{sale.version}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Business Insights</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Profit Margin:</Typography>
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {sale.subtotal > 0 ? ((sale.totalProfit / sale.subtotal) * 100).toFixed(1) : 0}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Items:</Typography>
                        <Typography variant="body2" fontWeight="600">{sale.items.length}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Quantity:</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {sale.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Action Dialogs */}
      <Dialog open={actionDialog.open && actionDialog.type === 'cancel'} onClose={() => setActionDialog({ open: false, type: '', sale: null })}>
        <DialogTitle>Cancel Sale</DialogTitle>
        <DialogContent>
          <Alert severity="warning">This action will restore stock and cannot be undone.</Alert>
          <Typography sx={{ mt: 1 }}>Are you sure you want to cancel invoice #{actionDialog.sale?.invoiceNumber}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', sale: null })}>No</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog.open && actionDialog.type === 'payment'} onClose={() => setActionDialog({ open: false, type: '', sale: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Update Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography>Invoice #: {actionDialog.sale?.invoiceNumber}</Typography>
            <Typography variant="h6">Grand Total: {currency(actionDialog.sale?.grandTotal || sale?.grandTotal || 0)}</Typography>
            <TextField select fullWidth label="Payment Method" value={paymentData.paymentMethod} onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))} sx={{ mt: 2 }}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="credit">Credit</MenuItem>
            </TextField>
            <TextField fullWidth type="number" label="Paid Amount" value={paymentData.paidAmount} onChange={(e) => setPaymentData(prev => ({ ...prev, paidAmount: parseFloat(e.target.value) || 0 }))} sx={{ mt: 2 }} />
            <Alert severity="info" sx={{ mt: 2 }}>Due: {currency(Math.max(0, (actionDialog.sale?.grandTotal || sale?.grandTotal || 0) - paymentData.paidAmount))}</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', sale: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmPaymentUpdate}>Update Payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SaleDetails;